// Proof-of-work for renewing the anonymous search token (SFDA-828): find a nonce such that
// sha256(`${jti}:${nonce}`) starts with `bits` zero bits. Verified by
// apps/api/app/services/search_tokens.py:pow_ok.
//
// A synchronous SHA-256 because crypto.subtle.digest is async per call, which is far too
// slow for a tight loop. Inputs are short ASCII strings; longer ones still work.

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
const H0 = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
  0x1f83d9ab, 0x5be0cd19,
];
const W = new Uint32Array(64);
const encoder = new TextEncoder();

/** Run the SHA-256 compression function over the 64-byte block at `offset` into `h`. */
function compress(h: Uint32Array, view: DataView, offset: number) {
  for (let i = 0; i < 16; i++) W[i] = view.getUint32(offset + i * 4);
  for (let i = 16; i < 64; i++) {
    const w15 = W[i - 15];
    const w2 = W[i - 2];
    const s0 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3);
    const s1 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10);
    W[i] = (W[i - 16] + s0 + W[i - 7] + s1) | 0;
  }
  let a = h[0];
  let b = h[1];
  let c = h[2];
  let d = h[3];
  let e = h[4];
  let f = h[5];
  let g = h[6];
  let hh = h[7];
  for (let i = 0; i < 64; i++) {
    const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
    const ch = (e & f) ^ (~e & g);
    const t1 = (hh + S1 + ch + K[i] + W[i]) | 0;
    const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
    const maj = (a & b) ^ (a & c) ^ (b & c);
    const t2 = (S0 + maj) | 0;
    hh = g;
    g = f;
    f = e;
    e = (d + t1) | 0;
    d = c;
    c = b;
    b = a;
    a = (t1 + t2) | 0;
  }
  h[0] += a;
  h[1] += b;
  h[2] += c;
  h[3] += d;
  h[4] += e;
  h[5] += f;
  h[6] += g;
  h[7] += hh;
}

/** The eight 32-bit words of sha256(input). */
export function sha256Words(input: string): Uint32Array {
  const bytes = encoder.encode(input);
  const blocks = Math.ceil((bytes.length + 9) / 64);
  const padded = new Uint8Array(blocks * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const bitLength = bytes.length * 8;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(padded.length - 4, bitLength >>> 0);

  const h = new Uint32Array(H0);
  for (let offset = 0; offset < padded.length; offset += 64) compress(h, view, offset);
  return h;
}

function hasLeadingZeroBits(words: Uint32Array, bits: number): boolean {
  let remaining = bits;
  for (let i = 0; remaining > 0; i++) {
    if (remaining >= 32) {
      if (words[i] !== 0) return false;
      remaining -= 32;
    } else {
      return words[i] >>> (32 - remaining) === 0;
    }
  }
  return true;
}

// Longest nonce in decimal digits (Number.MAX_SAFE_INTEGER has 16).
const MAX_NONCE_DIGITS = 16;

/** Try nonces start, start + 1, ... for `count` steps; the winning nonce, or null. */
export function solveRange(
  jti: string,
  bits: number,
  start: number,
  count: number,
): string | null {
  const prefix = encoder.encode(`${jti}:`);
  // `${jti}:${nonce}` plus padding must fit one 64-byte block (55 bytes of message at most)
  // for the fast path; real jtis are 22 characters, so this only guards odd inputs.
  if (prefix.length + MAX_NONCE_DIGITS > 55) {
    for (let n = start; n < start + count; n++) {
      if (hasLeadingZeroBits(sha256Words(`${jti}:${n}`), bits)) return String(n);
    }
    return null;
  }

  // One block reused for every nonce: the prefix is written once, the nonce digits, padding
  // and length are rewritten in place, so the loop allocates nothing.
  const block = new Uint8Array(64);
  const view = new DataView(block.buffer);
  const h = new Uint32Array(8);
  block.set(prefix);
  const end = start + count;
  for (let n = start; n < end; n++) {
    let digits = 1;
    for (let rest = Math.floor(n / 10); rest > 0; rest = Math.floor(rest / 10)) digits++;
    let rest = n;
    for (let i = prefix.length + digits - 1; i >= prefix.length; i--) {
      block[i] = 48 + (rest % 10);
      rest = Math.floor(rest / 10);
    }
    const length = prefix.length + digits;
    block[length] = 0x80;
    block.fill(0, length + 1, 60);
    view.setUint32(60, length * 8);
    h.set(H0);
    compress(h, view, 0);
    if (hasLeadingZeroBits(h, bits)) return String(n);
  }
  return null;
}
